import { Bell, CheckCircle, AlertTriangle, Clock, MessageSquare, FileCheck, UserPlus, Shield } from "lucide-react";
import { cn } from "@/lib/cn";
import type { NotificationType } from "@/domain/notification/notification.types";

const ICON_MAP: Record<NotificationType, React.ComponentType<{ className?: string }>> = {
  TASK_ASSIGNED: Bell,
  TASK_REASSIGNED: Bell,
  TASK_DUE_SOON: Clock,
  TASK_OVERDUE: AlertTriangle,
  TASK_COMPLETED: CheckCircle,
  TASK_REVIEW_REQUIRED: FileCheck,
  COMMENT_MENTION: MessageSquare,
  APPROVAL_REQUESTED: FileCheck,
  APPROVAL_APPROVED: CheckCircle,
  APPROVAL_REJECTED: AlertTriangle,
  MANAGEMENT_INSTRUCTION: Shield,
  COMPLIANCE_REMINDER: AlertTriangle,
};

const COLOR_MAP: Record<NotificationType, string> = {
  TASK_ASSIGNED: "bg-blue-50 text-blue-400 dark:bg-blue-900/20 dark:text-blue-300",
  TASK_REASSIGNED: "bg-blue-50 text-blue-400 dark:bg-blue-900/20 dark:text-blue-300",
  TASK_DUE_SOON: "bg-amber-50 text-amber-400 dark:bg-amber-900/20 dark:text-amber-300",
  TASK_OVERDUE: "bg-red-50 text-red-400 dark:bg-red-900/20 dark:text-red-300",
  TASK_COMPLETED: "bg-green-50 text-green-400 dark:bg-green-900/20 dark:text-green-300",
  TASK_REVIEW_REQUIRED: "bg-purple-50 text-purple-400 dark:bg-purple-900/20 dark:text-purple-300",
  COMMENT_MENTION: "bg-blue-50 text-blue-400 dark:bg-blue-900/20 dark:text-blue-300",
  APPROVAL_REQUESTED: "bg-amber-50 text-amber-400 dark:bg-amber-900/20 dark:text-amber-300",
  APPROVAL_APPROVED: "bg-green-50 text-green-400 dark:bg-green-900/20 dark:text-green-300",
  APPROVAL_REJECTED: "bg-red-50 text-red-400 dark:bg-red-900/20 dark:text-red-300",
  MANAGEMENT_INSTRUCTION: "bg-purple-50 text-purple-400 dark:bg-purple-900/20 dark:text-purple-300",
  COMPLIANCE_REMINDER: "bg-amber-50 text-amber-400 dark:bg-amber-900/20 dark:text-amber-300",
};

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function NotificationItem({
  notification,
  onRead,
  onDelete,
  onNavigateToTask,
}: {
  notification: { id: string; type: NotificationType; title: string; message: string; read: boolean; createdAt: string; taskId?: string | null };
  onRead: (id: string) => void;
  onDelete: (id: string) => void;
  onNavigateToTask?: (taskId: string) => void;
}) {
  const Icon = ICON_MAP[notification.type];
  const colorClass = COLOR_MAP[notification.type];

  const handleClick = () => {
    if (!notification.read) onRead(notification.id);
    if (notification.taskId && onNavigateToTask) {
      onNavigateToTask(notification.taskId);
    }
  };

  return (
    <div
      role={notification.taskId ? "button" : undefined}
      tabIndex={notification.taskId ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={(event) => {
        if (!notification.taskId) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        "flex w-full items-start gap-2.5 p-3 text-left transition-colors hover:bg-[var(--muted)]",
        !notification.read &&
          "bg-[var(--color-primary-soft)]/55 dark:bg-[color-mix(in_srgb,var(--color-primary)_12%,var(--card))]",
        notification.taskId && "cursor-pointer",
      )}
      aria-label={`${notification.title}. ${notification.read ? "Read" : "Unread"}`}
    >
      <div className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-full", colorClass)}>
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className={cn("text-xs font-medium text-[var(--foreground)]", !notification.read && "font-semibold")}>
            {notification.title}
          </p>
          {!notification.read && (
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-primary)]" aria-label="Unread" />
          )}
        </div>
        <p className="mt-0.5 text-[11px] text-[var(--muted-foreground)] line-clamp-2">
          {notification.message}
        </p>
        <p className="mt-0.5 text-[9px] text-[var(--color-mute)]">{timeAgo(notification.createdAt)}</p>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(notification.id);
        }}
        className="shrink-0 rounded p-0.5 text-[var(--color-mute)] hover:bg-[var(--muted)] hover:text-[var(--foreground)]"
        aria-label="Delete notification"
      >
        ×
      </button>
    </div>
  );
}
